import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import {ANONYMOUS_CREDENTIALS} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {PasswordResetRedeemPayload} from "../payload-types/password-reset";
import {WebError} from "../web-error";

/**
 * #19351: POST {@link RevaApi.PATH_PASSWORD_RESET_REDEEM} — redeems a password-reset token together with a
 * new password. Needs no credentials, so `execute` is overridden to always supply
 * {@link ANONYMOUS_CREDENTIALS} — never the caller's (possibly still valid, possibly stale) JWT.
 *
 * 422/503 are rethrown as the raw {@link WebError} instead of being converted to a generic `ErrorI18n`
 * (unlike the base class default), so the caller can show account-management's own `error`/`error_de`
 * message (422: link invalid/expired/already used, or the password policy) resp. the fixed
 * "not reachable" text (503).
 */
export class PasswordResetRedeemApiInterface extends SimpleApiInterface<object> {
    constructor(private readonly payload: PasswordResetRedeemPayload) {
        super();
    }

    async execute(api: ServiceApi): Promise<object> {
        return super.execute(api, ANONYMOUS_CREDENTIALS);
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_PASSWORD_RESET_REDEEM]);
    }

    protected buildPayload(): PasswordResetRedeemPayload {
        return this.payload;
    }

    protected executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<object> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }

    protected override handleApiErrors(e: Error): object {
        if (e instanceof WebError && (e.status === 422 || e.status === 503)) {
            throw e;
        }
        return super.handleApiErrors(e);
    }
}
